<!--
  告警日志：页头 + 筛选卡 + 表格卡 + 分页
  数据全部来自「告警日志列表」接口，请求 / 响应字段名与接口一致：
    请求 params：startTimeIso / endTimeIso / alertId / alertType / uf / sendStatus / recipientId / onlyFailed
    响应 items：ts / time / alertId / alertType / uf / subjectId / title / content / channel /
                recipientId / recipientName / sendStatus / retryCount / errorMsg / bizDate
-->
<template>
  <div class="alert-log">
    <!-- 页头 -->
    <div class="page-header">
      <div>
        <h2 style="margin-top: 0;">
          告警发送日志
          <span class="h2-pt">Log de Envios de Alertas</span>
        </h2>
        <div class="desc">
          <span>所有触发的告警事件，包括发送渠道、接收群、发送状态</span>
          <span class="desc-pt">
            Todos os alertas disparados, incluindo canal de envio, grupo destinatário e status do envio
          </span>
        </div>
      </div>
      <div class="right">
        <el-button size="small" @click="handleExport">
          <bi-label zh="导出" pt="Exportar" inline />
        </el-button>
      </div>
    </div>

    <!-- 筛选 -->
    <el-card class="filter-card" shadow="never">
      <el-form
        :model="searchParam" label-position="top"
        size="small"
      >
        <el-row :gutter="16">
          <el-col :span="6">
            <el-form-item>
              <template slot="label">
                <bi-label zh="告警时间" pt="Data do Alerta" inline />
              </template>
              <el-date-picker
                v-model="searchParam.params.time"
                type="daterange"
                value-format="yyyy-MM-dd"
                range-separator="~"
                start-placeholder="开始日期 / Início"
                end-placeholder="结束日期 / Fim"
                style="width: 100%"
              />
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item>
              <template slot="label">
                <bi-label zh="告警类型" pt="Tipo de Alerta" inline />
              </template>
              <el-select
                v-model="searchParam.params.alertType" placeholder="全部 / Todos" clearable
                style="width: 100%"
              >
                <el-option
                  v-for="o in typeOptions" :key="o.value" :label="o.label + ' / ' + o.labelPt"
                  :value="o.value"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item>
              <template slot="label">
                <bi-label zh="UF" inline />
              </template>
              <el-select
                v-model="searchParam.params.uf"
                placeholder="全部 / Todos"
                clearable
                multiple
                filterable
                collapse-tags
                style="width: 100%"
              >
                <el-option
                  v-for="uf in ufOptions" :key="uf.id" :label="uf.label"
                  :value="uf.label"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item>
              <template slot="label">
                <bi-label zh="发送状态" pt="Status do Envio" inline />
              </template>
              <el-select
                v-model="searchParam.params.sendStatus" placeholder="全部 / Todos" clearable
                style="width: 100%"
              >
                <el-option
                  v-for="o in statusOptions" :key="o.value" :label="o.label + ' / ' + o.labelPt"
                  :value="o.value"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item>
              <template slot="label">
                <bi-label zh="接收人" pt="Destinatário" inline />
              </template>
              <el-select
                v-model="searchParam.params.recipientId"
                placeholder="全部 / Todos"
                clearable
                filterable
                style="width: 100%"
              >
                <el-option
                  v-for="r in recipients" :key="r.id" :label="r.name"
                  :value="r.id"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item class="only-failed">
              <template slot="label">
                <span class="ph-label">&nbsp;</span>
              </template>
              <el-checkbox v-model="searchParam.params.onlyFailed">
                <bi-label zh="只看失败" pt="Somente falhas" inline />
              </el-checkbox>
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item class="filter-btns">
              <template slot="label">
                <span class="ph-label">&nbsp;</span>
              </template>
              <el-button size="small" type="primary" @click="applyFilter">
                <bi-label zh="查询" pt="Consultar" inline />
              </el-button>
              <el-button size="small" @click="resetFilter">
                <bi-label zh="重置" pt="Redefinir" inline />
              </el-button>
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
    </el-card>

    <!-- 表格 -->
    <el-card class="table-card" shadow="never">
      <el-table
        v-loading="tableLoading" :data="pagedRows"
        border
        size="mini"
        style="width: 100%"
      >
        <el-table-column min-width="180" class-name="cell-num">
          <template slot="header">
            <bi-label zh="告警时间" pt="Data do Alerta" />
          </template>
          <template slot-scope="{ row }">{{ fmtLogTime(row) }}</template>
        </el-table-column>
        <el-table-column min-width="170">
          <template slot="header">
            <bi-label zh="告警类型" pt="Tipo de Alerta" />
          </template>
          <template slot-scope="{ row }">
            <span class="tag" :class="typeCls(row.alertType)">
              <span class="dot" />{{ typeZh(row.alertType) }}
              <span class="tag-pt">/ {{ typePt(row.alertType) }}</span>
            </span>
          </template>
        </el-table-column>
        <el-table-column width="70">
          <template slot="header">
            <bi-label zh="UF" />
          </template>
          <template slot-scope="{ row }"><strong>{{ row.uf }}</strong></template>
        </el-table-column>
        <el-table-column min-width="300">
          <template slot="header">
            <bi-label zh="告警内容" pt="Conteúdo do Alerta" />
          </template>
          <template slot-scope="{ row }">
            <div class="al-title">{{ row.title || '—' }}</div>
            <div class="al-content">{{ row.content }}</div>
          </template>
        </el-table-column>
        <el-table-column min-width="150">
          <template slot="header">
            <bi-label zh="接收群" pt="Grupo Destinatário" />
          </template>
          <template slot-scope="{ row }">{{ row.recipientName || '—' }}</template>
        </el-table-column>
        <el-table-column width="110">
          <template slot="header">
            <bi-label zh="发送渠道" pt="Canal" />
          </template>
          <template slot-scope="{ row }">
            {{ row.channel }}
            <span class="chan-pt">{{ channelPt(row.channel) }}</span>
          </template>
        </el-table-column>
        <el-table-column min-width="180">
          <template slot="header">
            <bi-label zh="发送状态" pt="Status do Envio" />
          </template>
          <template slot-scope="{ row }">
            <span class="tag" :class="statusCls(row.sendStatus)">
              <span class="dot" />{{ statusZh(row.sendStatus) }}
              <span class="tag-pt">/ {{ statusPt(row.sendStatus) }}</span>
            </span>
            <div v-if="row.sendStatus === 2 && row.errorMsg" class="al-err">
              {{ row.errorMsg }}
            </div>
          </template>
        </el-table-column>
        <el-table-column width="100" align="center" class-name="cell-num">
          <template slot="header">
            <bi-label zh="重试次数" pt="Tentativas" />
          </template>
          <template slot-scope="{ row }">{{ row.retryCount }}</template>
        </el-table-column>
        <el-table-column min-width="160" show-overflow-tooltip class-name="cell-num">
          <template slot="header">
            <bi-label zh="告警 ID" pt="ID do Alerta" />
          </template>
          <template slot-scope="{ row }">{{ row.alertId }}</template>
        </el-table-column>
      </el-table>

      <!-- 分页：共 N 条 + 每页条数 + 翻页 + 前往页 -->
      <div class="pagination">
        <el-pagination
          :current-page.sync="searchParam.pageNumber"
          :page-size.sync="searchParam.pageSize"
          :page-sizes="[10, 20, 50]"
          :total="total"
          layout="slot, sizes, prev, pager, next, jumper"
          class="al-pagination"
          @current-change="handlePageChange"
          @size-change="handleSizeChange"
        >
          <span class="pb-total">
            共 {{ total }} 条<span class="pb-pt">/ Total: {{ total }} registros</span>
          </span>
        </el-pagination>
      </div>
    </el-card>

  </div>
</template>

<script>
import BiLabel from './BiLabel';
import { ALERT_TYPE_LABEL, SEND_STATUS_LABEL, todayStr } from '../mock';
import {
  getInvoiceSubjectStatusMonitorAlarmLogExport,
  getInvoiceSubjectStatusMonitorAlarmLogList,
  getInvoiceSubjectStatusMonitorRecipientList
} from '@/api/invoiceCenter';
import { goResourceTaskList } from '@/utils/goExportList';

export default {
  name: 'AlertLog',
  components: { BiLabel },
  props: {
    stateList: { type: Array, default: () => [] }
  },
  data() {
    return {
      // 查询参数结构同变更日志 / IE 信息管理：pageSize / pageNumber / params（键名与接口一致）
      searchParam: {
        pageSize: 10,
        pageNumber: 1,
        params: {
          // 时间区间默认当天
          time: [todayStr(), todayStr()],
          alertId: '',
          alertType: null,
          uf: [],
          sendStatus: null,
          recipientId: null,
          onlyFailed: false
        }
      },
      total: 0,
      tableLoading: false,
      pagedRows: [],
      // 接收人下拉：来自接收人列表接口（按 recipientId 筛选）
      recipients: []
    };
  },
  computed: {
    /** 告警类型选项：alertType 1紧急 2恢复 3警告 */
    typeOptions() {
      return Object.keys(ALERT_TYPE_LABEL).map(k => ({
        value: Number(k),
        label: ALERT_TYPE_LABEL[k].zh,
        labelPt: ALERT_TYPE_LABEL[k].pt
      }));
    },
    /** 发送状态选项：sendStatus 1成功 2失败 */
    statusOptions() {
      return Object.keys(SEND_STATUS_LABEL).map(k => ({
        value: Number(k),
        label: SEND_STATUS_LABEL[k].zh,
        labelPt: SEND_STATUS_LABEL[k].pt
      }));
    },
    /** UF 下拉选项：巴西 27 州 UF（静态字典） */
    ufOptions() {
      return this.stateList;
    }
  },
  mounted() {
    this.getTableData();
    this.fetchRecipients();
  },
  methods: {
    // ============ 接口 ============
    /**
     * 统一封装接口调用：失败只打日志并返回 null，由调用方提示
     * @param {string} label 接口名称（仅用于日志）
     * @param {Function} fn 真正的请求函数
     * @returns {Promise<object|null>} 成功返回 res，失败返回 null
     */
    async callApi(label, fn) {
      try {
        return await fn();
      } catch (e) {
        console.error(`[告警日志] ${label} 请求失败`, e);
        return null;
      }
    },
    /** 拉取告警日志：请求失败清空列表（空态）并提示，不做本地兜底 */
    async getTableData() {
      this.tableLoading = true;
      const param = {
        pageNumber: this.searchParam.pageNumber,
        pageSize: this.searchParam.pageSize,
        params: this.queryConditions()
      };
      const res = await this.callApi('page', () => getInvoiceSubjectStatusMonitorAlarmLogList(param));
      if (res && res.code === 200) {
        const data = res.data || {};
        const list = Array.isArray(data.items) ? data.items : [];
        this.pagedRows = list;
        // 接口 total 可能为 null（按游标翻页），此时用已加载条数兜底展示
        this.total = typeof data.total === 'number'
          ? data.total
          : (this.searchParam.pageNumber - 1) * this.searchParam.pageSize + list.length;
      } else {
        this.pagedRows = [];
        this.total = 0;
        this.$message.error('告警日志接口请求失败，请稍后重试 / Falha ao consultar o log de alertas');
      }
      this.tableLoading = false;
    },
    /** 拉取接收人下拉：失败只提示，下拉为空（不影响主列表） */
    async fetchRecipients() {
      const res = await this.callApi('recipients', () => getInvoiceSubjectStatusMonitorRecipientList());
      if (res && res.code === 200) {
        this.recipients = Array.isArray(res.data) ? res.data : [];
      } else {
        this.recipients = [];
        this.$message.error('接收人列表接口请求失败，接收人筛选暂不可用 / Falha ao consultar destinatários');
      }
    },

    // ============ 筛选 / 分页 ============
    /** 整理查询参数：字段名与接口一致，未填的条件置为 null */
    queryConditions() {
      const { time, alertId, alertType, uf, sendStatus, recipientId, onlyFailed } = this.searchParam.params;
      const [start, end] = Array.isArray(time) ? time : [null, null];
      return {
        // 时间区间转 ISO（巴西时区 -03:00）：开始日 00:00:00，结束日 23:59:59
        startTimeIso: this.toIso(start, false),
        endTimeIso: this.toIso(end, true),
        alertId: alertId ? String(alertId).trim() : null,
        alertType: alertType || null,
        uf: uf || [],
        sendStatus: sendStatus || null,
        recipientId: recipientId || null,
        onlyFailed: !!onlyFailed
      };
    },
    /** 日期 → ISO 字符串（yyyy-MM-ddTHH:mm:ss-03:00）；空值返回 null */
    toIso(day, isEnd) {
      if (!day) return null;
      return `${day}T${isEnd ? '23:59:59' : '00:00:00'}-03:00`;
    },
    /** 翻页：接口分页，切页时重新查询（不再本地切片） */
    handlePageChange(page) {
      this.searchParam.pageNumber = page;
      this.getTableData();
    },
    /** 切换每页条数：回到第 1 页重新查询 */
    handleSizeChange(size) {
      this.searchParam.pageSize = size;
      this.searchParam.pageNumber = 1;
      this.getTableData();
    },
    /** 点击查询：回到第 1 页重新请求 */
    applyFilter() {
      this.searchParam.pageNumber = 1;
      this.getTableData();
    },
    /** 重置：所有筛选项回到默认值并回到第 1 页 */
    resetFilter() {
      this.searchParam.params = {
        time: [todayStr(), todayStr()],
        alertId: '', alertType: null, uf: [], sendStatus: null, recipientId: null, onlyFailed: false
      };
      this.searchParam.pageNumber = 1;
      this.getTableData();
    },

    // ============ 单元格渲染 ============
    /** 告警类型中文 */
    typeZh(v) {
      return (ALERT_TYPE_LABEL[v] && ALERT_TYPE_LABEL[v].zh) || `类型 ${v}`;
    },
    /** 告警类型葡语 */
    typePt(v) {
      return (ALERT_TYPE_LABEL[v] && ALERT_TYPE_LABEL[v].pt) || '';
    },
    /** 告警类型标签配色：紧急红 / 恢复绿 / 警告橙 */
    typeCls(v) {
      return (ALERT_TYPE_LABEL[v] && ALERT_TYPE_LABEL[v].cls) || 'tag-gray';
    },
    /** 发送状态中文 */
    statusZh(v) {
      return (SEND_STATUS_LABEL[v] && SEND_STATUS_LABEL[v].zh) || '—';
    },
    /** 发送状态葡语 */
    statusPt(v) {
      return (SEND_STATUS_LABEL[v] && SEND_STATUS_LABEL[v].pt) || '';
    },
    /** 发送状态标签配色：成功绿 / 失败红 */
    statusCls(v) {
      return (SEND_STATUS_LABEL[v] && SEND_STATUS_LABEL[v].cls) || 'tag-gray';
    },
    /** 发送渠道葡语 */
    channelPt(channel) {
      return channel === '飞书' ? 'Feishu' : '';
    },

    // ============ 工具 ============
    /** 日志时间：优先用毫秒时间戳 ts，其次 ISO 时间 time，格式化为 yyyy-MM-dd HH:mm:ss */
    fmtLogTime(row) {
      const src = row.ts ? new Date(Number(row.ts)) : (row.time ? new Date(row.time) : null);
      if (!src || isNaN(src.getTime())) return row.time || '—';
      const p = n => String(n).padStart(2, '0');
      return `${src.getFullYear()}-${p(src.getMonth() + 1)}-${p(src.getDate())} ${p(src.getHours())}:${p(src.getMinutes())}:${p(src.getSeconds())}`;
    },

    // ============ 操作 ============
    /** 导出当前筛选结果（暂无导出接口，只提示条数） */
    handleExport() {
      getInvoiceSubjectStatusMonitorAlarmLogExport(this.queryConditions()).then(res => {
        if (res.code === 200) {
          goResourceTaskList();
        }
      });
    }
  }
};
</script>

<style scoped lang="scss">
.alert-log {
  padding: 4px 0 20px;
}
.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 16px;
  gap: 16px;
}
.page-header h2 {
  font-size: 18px;
  font-weight: 600;
  display: flex;
  flex-direction: column;
}
.page-header h2 .h2-pt {
  font-size: 12px;
  font-weight: 400;
  color: #86909c;
}
.page-header .desc {
  color: #86909c;
  font-size: 12px;
  margin-top: 4px;
  display: flex;
  flex-direction: column;
}
.page-header .desc .desc-pt {
  font-size: 12px;
}
.page-header .right {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

.filter-card {
  margin-bottom: 12px;
  border-radius: 8px;
}
.table-card {
  border-radius: 8px;
}

::v-deep .el-card__body { padding: 20px; }
::v-deep .el-form-item { margin-bottom: 0; }
::v-deep .el-form-item__label { padding-bottom: 4px; line-height: 20px; }
.ph-label { display: block; }
::v-deep .filter-btns .el-form-item__content { display: flex; }
/* 日期区间选择器宽度自适应（默认较宽，会挤掉同一行的其他项） */
::v-deep .el-date-editor--daterange.el-input__inner { width: 100%; }
/* 「只看失败」与操作按钮同高对齐 */
::v-deep .only-failed .el-form-item__content { line-height: 32px; }

::v-deep .cell-num .cell {
  font-family: ui-monospace, 'SF Mono', Menlo, Consolas, monospace;
  font-size: 14px;
}
::v-deep .el-table th .cell { line-height: 1.35; }

.tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  font-size: 12px;
  font-weight: 500;
  border-radius: 4px;
  line-height: 18px;
  white-space: nowrap;
}
.tag .dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
}
.tag .tag-pt {
  font-size: 12px;
  font-weight: 400;
}
.tag-ok { background: #e8f7e9; color: #00b42a; }
.tag-err { background: #ffecec; color: #f53f3f; }
.tag-warn { background: #fff3e0; color: #ff7d00; }
.tag-info { background: #e6f0ff; color: #165dff; }
.tag-gray { background: #f2f3f5; color: #4e5969; }

.al-title {
  font-size: 12px;
  color: #1d2129;
  font-weight: 600;
  line-height: 1.5;
}
.al-content {
  font-size: 12px;
  color: #4e5969;
  line-height: 1.5;
  margin-top: 2px;
  white-space: pre-wrap;
  word-break: break-word;
}
.al-err {
  font-size: 12px;
  color: #f53f3f;
  margin-top: 2px;
  line-height: 1.5;
  word-break: break-word;
}
.chan-pt {
  display: block;
  font-size: 12px;
  color: #86909c;
}

.empty-row {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 32px 12px;
  color: #86909c;
  font-size: 14px;
}
.empty-row .empty-pt {
  font-size: 14px;
}

/* 分页：el-pagination（总数走自定义 slot，保留中葡双语） */
.al-pagination {
  padding: 14px 0 2px;
  font-size: 12px;
  color: #4e5969;
}
.pb-total {
  display: flex;
  flex-direction: column;
  font-weight: 400;
  line-height: 1.35;
}
.pb-total .pb-pt {
  font-size: 12px;
  color: #86909c;
}
::v-deep .al-pagination .el-pagination__sizes { margin: 0 8px 0 12px; }
::v-deep .al-pagination .btn-prev,
::v-deep .al-pagination .btn-next,
::v-deep .al-pagination .el-pager li,
::v-deep .al-pagination .el-input__inner {
  background: #fff;
  border: 1px solid #e5e6eb;
  border-radius: 4px;
  min-width: 28px;
  height: 28px;
  line-height: 26px;
  margin: 0 3px;
  font-weight: 400;
}
::v-deep .al-pagination .el-pager li.active {
  background: #00a551;
  border-color: #00a551;
  color: #fff;
}
::v-deep .al-pagination .el-pagination__jump { margin-left: 8px; }
</style>
